import {
  ADD_PANEL,
  ADD_WORKSPACE,
  AUTHENTICATE,
  CLONE_PANEL,
  DEAUTHENTICATE,
  DELETE_WORKSPACE,
  DISMISS_NOTIFICATION,
  NOTIFY,
  PUSH_WORKSPACE_TO_RECENT,
  REMOVE_PANEL,
  REORDER_PANELS,
  SET_ACTIVE_DATASETS,
  SET_CHARACTERISTICS,
  SET_FILES,
  SET_LAYERS,
  SET_LOADING,
  SET_METADATA_TYPES,
  SET_MODELS,
  SET_PANEL_OPTS,
  SET_PANEL_RAW_DATA,
  SET_PANEL_SELECTION,
  SET_REGIONS,
  SET_RUNS,
  SET_SCENARIOS,
  SET_SKIP_TOUR,
  SET_TIMESLICES,
  SET_UNITS,
  SET_USER_CONFIG,
  SET_USER_PROFILE,
  SET_WORKSPACE_PREVIEW,
  SET_WORKSPACES,
  SET_WORKSPACES_FILTER,
  TOGGLE_DARK_THEME,
  TOGGLE_PANEL_SIZE,
  TOGGLE_WORKSPACES_FILTER,
  UPDATE_WORKSPACE,
} from './mutation-types';
import axios from 'axios/index';
import {by, sortByAttrs} from '../utilities';
import {updateWorkspaceFormat} from '../utilities/scenario-explorer';
import {defaultState} from './index';
import {cloneDeep, get} from 'lodash';
import Vue from 'vue';

function resetState(state) {
  // keep dark theme setting on logout
  const useDarkTheme = get(state, 'ui.useDarkTheme', false);
  Object.entries(defaultState).forEach(([key, value]) => {
    Vue.set(state, key, cloneDeep(value));
  });
  state.ui.useDarkTheme = useDarkTheme;
}

export default {
  [SET_WORKSPACES](state, workspaces) {
    state.workspaces = updateWorkspaceFormat(workspaces);
  },
  [ADD_WORKSPACE](state, workspace) {
    state.workspaces = [...state.workspaces.filter(w => w.id !== workspace.id), workspace];
  },
  [DELETE_WORKSPACE](state, workspace) {
    state.workspaces = [...state.workspaces.filter(w => w.id !== workspace.id)];
  },
  [AUTHENTICATE](state, {username, token, profile, config}) {
    resetState(state);
    state.auth = {username, token, profile, config};
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
  },
  [DEAUTHENTICATE](state) {
    resetState(state);
    axios.defaults.headers.common['Authorization'] = undefined;
  },
  [NOTIFY](state, {message, variant, countDown, clean = false}) {
    if (clean) {
      state.notifications = [{message, variant, countDown}];
    } else {
      state.notifications.push({message, variant, countDown});
    }
  },
  [DISMISS_NOTIFICATION](state, notification) {
    if (notification === undefined) {
      state.notifications.splice(0, state.notifications.length);
    } else {
      state.notifications = state.notifications.filter(n => n !== notification);
    }
  },
  [UPDATE_WORKSPACE](state, {workspace}) {
    state.workspaces = state.workspaces.map(w => w.id === workspace.id ? {...w, ...workspace} : w);
  },
  [SET_WORKSPACE_PREVIEW](state, {workspaceId, preview}) {
    const {workspaces, previews} = state;
    const workspace = workspaces.find(by('id', workspaceId));
    if (workspace) {
      if (preview) {
        previews[workspaceId] = preview;
      } else {
        delete previews[workspaceId];
      }
      workspace.hasPreview = !!preview;
    }
  },
  [SET_LOADING](state, loading) {
    Object.entries(loading).forEach(([key, value]) => {
      Vue.set(state.loading, key, value);
    });
  },
  [SET_UNITS](state, units) {
    state.data.units = Object.freeze(units);
  },
  [SET_TIMESLICES](state, timeslices) {
    Vue.set(state.data, 'timeslices', Object.freeze(timeslices));
  },
  [SET_REGIONS](state, regions) {
    state.data.regions = Object.freeze(regions);
  },
  [SET_MODELS](state, models) {
    state.data.models = Object.freeze(models);
  },
  [SET_SCENARIOS](state, scenarios) {
    state.data.scenarios = Object.freeze(scenarios);
  },
  [SET_RUNS](state, runs) {
    state.data.runs = Object.freeze(runs);
  },
  [SET_LAYERS](state, layers) {
    state.data.layers = layers;
  },
  [SET_CHARACTERISTICS](state, characteristics) {
    Object.getOwnPropertyNames(characteristics).forEach(name => Object.freeze(characteristics[name]));
    state.data.characteristics = Object.freeze(characteristics);
  },
  [SET_METADATA_TYPES](state, metadataTypes) {
    state.data.metadataTypes = Object.freeze(metadataTypes);
  },
  [SET_FILES](state, files) {
    state.data.files = Object.freeze(files);
  },
  [ADD_PANEL](state, {workspaceId, panel}) {
    state.workspaces = state.workspaces.map(w => w.id === workspaceId ? {
      ...w,
      panels: [
        ...w.panels,
        {
          ...panel,
          id: Math.max(0, ...w.panels.map(f => f.id)) + 1,
        },
      ]
    } : w);
  },
  [CLONE_PANEL](state, {workspaceId, panel}) {
    state.workspaces = state.workspaces.map(w => w.id === workspaceId ? {
      ...w,
      panels: [
        ...w.panels,
        {
          ...JSON.parse(JSON.stringify(panel)),
          id: Math.max(0, ...w.panels.map(f => f.id)) + 1,
        },
      ],
    } : w);
  },
  [REMOVE_PANEL](state, {workspaceId, panel}) {
    state.workspaces = state.workspaces.map(w => w.id === workspaceId ? {
      ...w,
      panels: w.panels.filter(f => f !== panel),
    } : w);
  },
  [REORDER_PANELS](state, {workspaceId, order}) {
    state.workspaces = state.workspaces.map(w => w.id === workspaceId ? {
      ...w,
      panels: w.panels.sort(sortByAttrs([f => order.indexOf(f.id), 1])),
    } : w);
  },
  [TOGGLE_PANEL_SIZE](state, panel) {
    state.workspaces = state.workspaces.map(w => ({
      ...w,
      panels: w.panels.map(f => f !== panel ? f : ({
        ...f,
        options: {
          ...f.options,
          size: f.options.size === 'full' ? 'half' : 'full',
        },
      })),
    }));
  },
  [SET_PANEL_SELECTION](state, {workspaceId, panelId, selection}) {
    state.workspaces = state.workspaces.map(workspace => workspace.id === workspaceId ? {
      ...workspace,
      panels: workspace.panels.map(panel => {
        return panel.id === panelId ? {
          ...panel,
          selection: Object.freeze(selection),
        } : panel;
      })
    } : workspace);
  },
  [SET_PANEL_RAW_DATA](state, {workspaceId, panelId, raw, activeSelection = {}}) {
    state.workspaces = state.workspaces.map(workspace => workspace.id === workspaceId ? {
      ...workspace,
      panels: workspace.panels.map(panel => {
        return panel.id === panelId ? {
          ...panel,
          raw: Object.freeze(raw),
          activeSelection: Object.freeze(activeSelection),
        } : panel;
      })
    } : workspace);
  },
  [SET_PANEL_OPTS](state, {workspaceId, panelId, options}) {
    state.workspaces = state.workspaces.map(workspace => workspace.id === workspaceId ? {
      ...workspace,
      panels: workspace.panels.map(panel => {
        return panel.id === panelId ? {
          ...panel,
          options: Object.freeze(options),
        } : panel;
      })
    } : workspace);
  },
  [SET_ACTIVE_DATASETS](state, {keys}) {
    state.interaction.activeDatasets = keys;
  },
  [SET_WORKSPACES_FILTER](state, workspacesFilter) {
    state.ui.workspacesFilter = workspacesFilter;
  },
  [TOGGLE_WORKSPACES_FILTER](state) {
    state.ui.workspacesFilterExpanded = !state.ui.workspacesFilterExpanded;
  },
  [SET_SKIP_TOUR](state, skipTour) {
    state.auth.config.skipTour = skipTour;
  },
  [TOGGLE_DARK_THEME](state) {
    state.ui.useDarkTheme = !state.ui.useDarkTheme;
  },
  [PUSH_WORKSPACE_TO_RECENT](state, recentWorkspaceId) {
    const MAX_RECENT = 5;
    const ids = new Set(state.workspaces.map(w => w.id));
    const rest = state.ui.workspaceSelector.recent.filter(id => ids.has(id) && id !== recentWorkspaceId);
    state.ui.workspaceSelector.recent = [recentWorkspaceId, ...rest.slice(0, MAX_RECENT - 1)];
  },
  [SET_USER_PROFILE](state, profile) {
    state.auth.profile = profile;
  },
  [SET_USER_CONFIG](state, config) {
    state.auth.config = config;
  },
};
